/**
 * THROWAWAY DEMO CODE (Pillar procurement demo).
 *
 * Deterministic document search that answers a free-text question on an
 * agreement's page in the AI assistant: BM25-lite over document sections with a
 * small procurement synonym map, then a templated answer that quotes the
 * strongest sentences. No network.
 */
import type {
  AskCitation,
  AskConfidence,
  AskDocument,
  AskLocalAnswer,
  AskSection,
} from './types';

const MAX_SECTIONS = 3;
const MAX_BULLETS = 4;
const MAX_SENTENCE_CHARS = 240;
const MAX_QUOTE_CHARS = 200;
/** A section must score at least this to count as a match at all. */
const MIN_SECTION_SCORE = 0.4;
/** Sections below this share of the best score are not shown. */
const RELATIVE_SCORE_CUTOFF = 0.5;
const SYNONYM_WEIGHT = 0.5;
const HEADING_BOOST = 2;
const REF_BOOST = 3;
const BM25_K1 = 1.2;
const BM25_B = 0.75;

/** Words that carry no meaning for ranking. Applied to the question only. */
const QUERY_STOPWORDS = new Set([
  'a',
  'about',
  'all',
  'also',
  'am',
  'an',
  'and',
  'any',
  'are',
  'as',
  'at',
  'be',
  'been',
  'but',
  'by',
  'can',
  'could',
  'did',
  'do',
  'does',
  'for',
  'from',
  'get',
  'give',
  'has',
  'have',
  'how',
  'i',
  'if',
  'in',
  'into',
  'is',
  'it',
  'its',
  'me',
  'much',
  'my',
  'of',
  'on',
  'or',
  'our',
  'please',
  'show',
  'tell',
  'than',
  'that',
  'the',
  'their',
  'them',
  'there',
  'these',
  'they',
  'this',
  'to',
  'under',
  'us',
  'was',
  'we',
  'were',
  'what',
  'when',
  'where',
  'which',
  'who',
  'whom',
  'will',
  'with',
  'would',
  'you',
  'your',
  // Question filler that never names a contract concept.
  'down',
  'go',
  'goes',
  'many',
  'may',
  'might',
  'need',
  'needs',
  'out',
  'over',
  'should',
  'some',
  'up',
  'want',
  // Meta words that name the thing being searched, not what to find in it.
  'agreement',
  'contract',
  'clause',
  'document',
  'section',
  // "SLA terms", "payment terms": the word is filler, expansions re-add it.
  'terms',
]);

interface SynonymGroup {
  /** Question words that switch this group on. */
  triggers: string[];
  /** Extra words (or phrases) to look for in the documents. */
  expansions: string[];
}

const SYNONYM_GROUPS: SynonymGroup[] = [
  {
    triggers: [
      'sla',
      'uptime',
      'availability',
      'downtime',
      'outage',
      'level',
      'levels',
      'promise',
    ],
    expansions: [
      'uptime',
      'service level',
      'availability',
      'credits',
      'response',
    ],
  },
  {
    triggers: ['payment', 'invoice', 'invoicing', 'net'],
    expansions: ['payment', 'net', 'terms', 'invoice', 'due'],
  },
  {
    triggers: ['renew', 'renewal', 'renewing', 'autorenew'],
    expansions: ['renewal', 'auto-renew', 'notice', 'term'],
  },
  {
    triggers: [
      'liability',
      'liable',
      'indemnity',
      'indemnify',
      'indemnification',
    ],
    expansions: ['liability', 'cap', 'limitation'],
  },
  {
    triggers: [
      'terminate',
      'termination',
      'terminating',
      'cancel',
      'cancellation',
    ],
    expansions: ['termination', 'convenience', 'notice'],
  },
  {
    triggers: [
      'price',
      'pricing',
      'cost',
      'increase',
      'escalation',
      'adjustment',
      'index',
    ],
    expansions: ['pricing', 'adjustment', 'escalation', 'increase', 'index'],
  },
  {
    triggers: ['tariff', 'duty', 'duties', 'import', 'customs'],
    expansions: ['duties', 'taxes', 'ddp', 'import', 'tariff'],
  },
  {
    triggers: [
      'freight',
      'shipping',
      'delivery',
      'delivered',
      'incoterms',
      'dap',
      'ddp',
    ],
    expansions: ['incoterms', 'dap', 'delivery', 'shipping', 'freight'],
  },
  {
    triggers: ['rebate', 'discount', 'volume'],
    expansions: ['volume', 'credit', 'rebate'],
  },
  {
    triggers: ['quality', 'ppm', 'coa', 'ncr', 'nonconformance', 'defect'],
    expansions: ['ppm', 'coa', 'nonconformance', 'change', 'quality'],
  },
  {
    triggers: [
      'data',
      'privacy',
      'dpa',
      'residency',
      'breach',
      'gdpr',
      'security',
    ],
    expansions: ['privacy', 'dpa', 'residency', 'breach', 'training', 'data'],
  },
  {
    triggers: ['waiting', 'detention', 'accessorial', 'extra'],
    expansions: ['detention', 'accessorial', 'charge', 'proof'],
  },
  {
    triggers: ['budget', 'hourly', 'rates', 'rate'],
    expansions: ['rate', 'card', 'exceed', 'amount'],
  },
  {
    triggers: ['motor', 'drive', 'drives'],
    expansions: ['variable', 'frequency', 'drive'],
  },
  {
    triggers: ['subcontractor', 'subcontractors'],
    expansions: ['sub', 'processor', 'notice'],
  },
  {
    triggers: ['recovery', 'restore', 'restored'],
    expansions: ['rpo', 'rto', 'recovery', 'objective'],
  },
  {
    triggers: ['repeated', 'repeatedly', 'chronic'],
    expansions: ['chronic', 'failure', 'terminate'],
  },
];

/** Lowercase word tokens. Keeps "99.9", "4.2" and "b.3" intact. */
export function tokenize(text: string): string[] {
  const normalized = text.toLowerCase().replace(/(?<=\d),(?=\d{3}\b)/g, '');
  return normalized.match(/[a-z0-9]+(?:\.[0-9]+)?/g) ?? [];
}

/** Tiny suffix stemmer: enough to match "renews/renewed", "credits/credit". */
export function stem(word: string): string {
  if (/^\d/.test(word)) return word;
  let result = word;
  if (result.length > 4 && result.endsWith('ies')) {
    result = `${result.slice(0, -3)}y`;
  } else if (result.length > 4 && result.endsWith('sses')) {
    result = result.slice(0, -2);
  } else if (
    result.length > 3 &&
    result.endsWith('s') &&
    !result.endsWith('ss') &&
    !result.endsWith('us') &&
    !result.endsWith('is')
  ) {
    result = result.slice(0, -1);
  }
  if (result.length > 5 && result.endsWith('ing')) {
    result = result.slice(0, -3);
  } else if (result.length > 4 && result.endsWith('ed')) {
    result = result.slice(0, -2);
  }
  if (result.length > 3 && result.endsWith('e')) {
    result = result.slice(0, -1);
  }
  return result;
}

function stemmedTokens(text: string): string[] {
  return tokenize(text).map(stem);
}

/** Stemmed question words worth searching for (stopwords removed, deduplicated). */
export function contentTerms(question: string): string[] {
  const terms = tokenize(question)
    .filter((token) => !QUERY_STOPWORDS.has(token))
    .map(stem);
  return Array.from(new Set(terms));
}

const EXPANSIONS_BY_TRIGGER: ReadonlyMap<string, readonly string[]> = (() => {
  const map = new Map<string, Set<string>>();
  for (const group of SYNONYM_GROUPS) {
    const expansionTerms = group.expansions.flatMap(stemmedTokens);
    for (const trigger of group.triggers) {
      const key = stem(trigger);
      const existing = map.get(key) ?? new Set<string>();
      for (const term of expansionTerms) existing.add(term);
      map.set(key, existing);
    }
  }
  const result = new Map<string, readonly string[]>();
  for (const [key, terms] of map) result.set(key, Array.from(terms));
  return result;
})();

interface QueryMember {
  term: string;
  weight: number;
}

/** One question word plus the synonyms that may stand in for it. */
interface QueryGroup {
  members: QueryMember[];
}

function buildQueryGroups(question: string): QueryGroup[] {
  return contentTerms(question).map((term) => {
    const members: QueryMember[] = [{ term, weight: 1 }];
    for (const expansion of EXPANSIONS_BY_TRIGGER.get(term) ?? []) {
      if (expansion !== term) {
        members.push({ term: expansion, weight: SYNONYM_WEIGHT });
      }
    }
    return { members };
  });
}

interface IndexedSection {
  document: AskDocument;
  section: AskSection;
  termFrequency: Map<string, number>;
  length: number;
}

interface SectionIndex {
  sections: IndexedSection[];
  documentFrequency: Map<string, number>;
  averageLength: number;
}

function addTerms(
  frequency: Map<string, number>,
  terms: string[],
  boost: number,
): number {
  for (const term of terms) {
    frequency.set(term, (frequency.get(term) ?? 0) + boost);
  }
  return terms.length * boost;
}

function buildIndex(documents: AskDocument[]): SectionIndex {
  const sections: IndexedSection[] = [];
  const documentFrequency = new Map<string, number>();

  for (const document of documents) {
    const titleTerms = stemmedTokens(
      `${document.title} ${document.shortTitle}`,
    );
    for (const section of document.sections) {
      const termFrequency = new Map<string, number>();
      let length = 0;
      length += addTerms(termFrequency, stemmedTokens(section.text), 1);
      length += addTerms(
        termFrequency,
        stemmedTokens(section.heading),
        HEADING_BOOST,
      );
      length += addTerms(termFrequency, stemmedTokens(section.ref), REF_BOOST);
      length += addTerms(termFrequency, titleTerms, 1);
      for (const term of termFrequency.keys()) {
        documentFrequency.set(term, (documentFrequency.get(term) ?? 0) + 1);
      }
      sections.push({ document, section, termFrequency, length });
    }
  }

  const totalLength = sections.reduce((sum, item) => sum + item.length, 0);
  return {
    sections,
    documentFrequency,
    averageLength: sections.length > 0 ? totalLength / sections.length : 0,
  };
}

function inverseDocumentFrequency(index: SectionIndex, term: string): number {
  const documentFrequency = index.documentFrequency.get(term) ?? 0;
  const total = index.sections.length;
  return Math.log(
    1 + (total - documentFrequency + 0.5) / (documentFrequency + 0.5),
  );
}

function bm25(index: SectionIndex, item: IndexedSection, term: string): number {
  const frequency = item.termFrequency.get(term) ?? 0;
  if (frequency === 0) return 0;
  const lengthNorm =
    1 - BM25_B + BM25_B * (item.length / Math.max(index.averageLength, 1));
  const saturation =
    (frequency * (BM25_K1 + 1)) / (frequency + BM25_K1 * lengthNorm);
  return inverseDocumentFrequency(index, term) * saturation;
}

export interface RankedSection {
  document: AskDocument;
  section: AskSection;
  score: number;
  /** Rarity-weighted share of question words (or a synonym) found in the section, 0 to 1. */
  coverage: number;
}

/** Ranks every section of every document against the question, best first. */
export function rankSections(
  question: string,
  documents: AskDocument[],
): RankedSection[] {
  const groups = buildQueryGroups(question);
  if (groups.length === 0) return [];
  const index = buildIndex(documents);

  // Rare question words count more toward coverage than words every section
  // has, and words found nowhere in the documents are ignored (they say
  // nothing about how well a section fits).
  const groupWeights = groups.map((group) =>
    Math.max(
      0,
      ...group.members
        .filter((member) => index.documentFrequency.has(member.term))
        .map((member) => inverseDocumentFrequency(index, member.term)),
    ),
  );
  const totalWeight = groupWeights.reduce((sum, weight) => sum + weight, 0);

  const ranked: RankedSection[] = [];
  for (const item of index.sections) {
    let score = 0;
    let matchedWeight = 0;
    for (const [position, group] of groups.entries()) {
      let groupMatched = false;
      for (const member of group.members) {
        const memberScore = bm25(index, item, member.term);
        if (memberScore > 0) {
          groupMatched = true;
          score += memberScore * member.weight;
        }
      }
      if (groupMatched) matchedWeight += groupWeights[position] ?? 0;
    }
    if (score > 0) {
      ranked.push({
        document: item.document,
        section: item.section,
        score,
        coverage: totalWeight > 0 ? matchedWeight / totalWeight : 0,
      });
    }
  }
  return ranked.sort((a, b) => b.score - a.score);
}

/** Plain text safe to drop into our mini-markdown answers. */
function cleanForQuote(text: string): string {
  return text
    .replace(/[*_`]/g, '')
    .replace(/^[#>\s]+/, '')
    .replace(/^(?:[-•]|\(?[a-z0-9]{1,2}[.)])\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function clip(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const cut = text.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxChars)}...`;
}

function splitSentences(text: string): string[] {
  return text
    .split(/\n+|(?<=[.!?;])\s+(?=[A-Z(\d])/)
    .map(cleanForQuote)
    .filter((sentence) => sentence.length > 0);
}

interface ScoredSentence {
  text: string;
  score: number;
  order: number;
}

function scoreSentences(
  sentences: string[],
  groups: QueryGroup[],
  index: SectionIndex,
): ScoredSentence[] {
  return sentences.map((text, order) => {
    const terms = new Set(stemmedTokens(text));
    let score = 0;
    for (const group of groups) {
      let best = 0;
      for (const member of group.members) {
        if (terms.has(member.term)) {
          best = Math.max(
            best,
            inverseDocumentFrequency(index, member.term) * member.weight,
          );
        }
      }
      score += best;
    }
    return { text, score, order };
  });
}

/** Strongest sentences of a section, in document order. */
function pickSentences(
  section: AskSection,
  groups: QueryGroup[],
  index: SectionIndex,
  limit: number,
): string[] {
  const scored = scoreSentences(
    splitSentences(section.text),
    groups,
    index,
  ).sort((a, b) => b.score - a.score || a.order - b.order);
  const best = scored[0];
  if (!best) return [];
  const chosen = scored
    .filter(
      (sentence) => sentence.score > 0 && sentence.score >= best.score * 0.5,
    )
    .slice(0, limit);
  const fallback = chosen.length > 0 ? chosen : [best];
  return fallback
    .sort((a, b) => a.order - b.order)
    .map((sentence) => clip(sentence.text, MAX_SENTENCE_CHARS));
}

function confidenceFor(top: RankedSection): AskConfidence {
  if (top.coverage >= 0.75) return 'high';
  if (top.coverage >= 0.4) return 'medium';
  return 'low';
}

/**
 * The share of the question's words (or a synonym of each) that appear in the
 * documents' text at all, 0 to 1. A question about something the agreement
 * never mentions ("the weather in Dallas") scores low even when one of its
 * words happens to match.
 */
export function questionCoverage(
  question: string,
  documents: AskDocument[],
): number {
  const groups = buildQueryGroups(question);
  if (groups.length === 0) return 0;
  const known = new Set<string>();
  for (const document of documents) {
    for (const section of document.sections) {
      for (const term of stemmedTokens(`${section.heading} ${section.text}`)) {
        known.add(term);
      }
    }
  }
  const found = groups.filter((group) =>
    group.members.some((member) => known.has(member.term)),
  );
  return found.length / groups.length;
}

const NOT_FOUND_ANSWER =
  'I could not find this in the provided documents. Try asking about a specific clause, such as payment terms, liability or termination.';

/**
 * Answers from the documents alone, no model. Returns the top sections as a
 * one-sentence lead plus bullets that quote the strongest sentences.
 */
export function answerLocally(
  question: string,
  documents: AskDocument[],
): AskLocalAnswer {
  const ranked = rankSections(question, documents);
  const top = ranked[0];
  if (!top || top.score < MIN_SECTION_SCORE) {
    return { answer: NOT_FOUND_ANSWER, citations: [], confidence: 'low' };
  }

  const groups = buildQueryGroups(question);
  const index = buildIndex(documents);
  const selected = ranked
    .filter((item) => item.score >= top.score * RELATIVE_SCORE_CUTOFF)
    .slice(0, MAX_SECTIONS);
  const confidence = confidenceFor(top);

  const lead =
    confidence === 'low'
      ? 'I could not find a direct answer. The closest sections are below.'
      : `The closest match is **${cleanForQuote(top.section.heading)}** in the ${top.document.shortTitle} (§${top.section.ref}).`;

  const bullets: string[] = [];
  const citations: AskCitation[] = [];
  for (const [position, item] of selected.entries()) {
    const sentences = pickSentences(
      item.section,
      groups,
      index,
      position === 0 ? 2 : 1,
    );
    for (const sentence of sentences) {
      if (bullets.length < MAX_BULLETS) {
        bullets.push(
          `- ${sentence} (${item.document.shortTitle} §${item.section.ref})`,
        );
      }
    }
    citations.push({
      documentId: item.document.id,
      ref: item.section.ref,
      quote: clip(
        sentences[0] ?? cleanForQuote(item.section.text),
        MAX_QUOTE_CHARS,
      ),
    });
  }

  return {
    answer: [lead, bullets.join('\n')].join('\n\n'),
    citations,
    confidence,
  };
}
